import type * as React from 'react';
import { CodigoBarras } from '@/componentes/etiqueta/CodigoBarras';

/** Um volume da etiqueta — o que a API devolve da UDF do ERP. */
export interface VolumeDanfe {
  volume: string | null;
  ean: string | null;
  nfe: string | null;
  serie: string | null;
  movimento: string | null;
  chaveNfe: string | null;
  nomeRem: string | null;
  enderecoRem: string | null;
  cidadeRem: string | null;
  estadoRem: string | null;
  cepRem: string | null;
  cnpjRem: string | null;
  ieRem: string | null;
  total: number | null;
  nomeDes: string | null;
  enderecoDes: string | null;
  complementoDes: string | null;
  referencia: string | null;
  cidadeDes: string | null;
  estadoDes: string | null;
  bairroDes: string | null;
  cepDes: string | null;
  pedidoMarketplace: string | null;
  transportadora: string | null;
  televenda: number;
}

/*
 * Onde o desenho começa dentro da etiqueta de 100 × 150 mm.
 *
 * O .rtm foi feito para papel de 110 mm com margem de 6,35 mm; o conteúdo tem
 * 97,4 × 135,5 mm. Na etiqueta de 100 mm, o mesmo desenho entra com margem
 * pequena dos dois lados e sobra embaixo.
 */
const X0 = 1.3;
const Y0 = 3;

/** Posição e tamanho em MILÉSIMOS de mm, como estão no .rtm. */
function caixa(left: number, top: number, width: number, height: number): React.CSSProperties {
  return {
    position: 'absolute',
    left: `${X0 + left / 1000}mm`,
    top: `${Y0 + top / 1000}mm`,
    width: `${width / 1000}mm`,
    height: `${height / 1000}mm`,
    overflow: 'hidden',
    lineHeight: 1.12,
    whiteSpace: 'nowrap',
  };
}

const quebra: React.CSSProperties = { whiteSpace: 'normal' };
const negrito: React.CSSProperties = { fontWeight: 700 };
/** Faixa preta com letra branca — os títulos de bloco do .rtm. */
const faixa: React.CSSProperties = { background: '#000', color: '#fff', fontWeight: 700 };

function Texto({
  em,
  pt,
  estilo,
  children,
}: {
  em: [number, number, number, number];
  pt: number;
  estilo?: React.CSSProperties;
  children: React.ReactNode;
}): JSX.Element {
  return <div style={{ ...caixa(...em), fontSize: `${pt}pt`, ...estilo }}>{children}</div>;
}

/**
 * Código de barras com o número embaixo, como o ReportBuilder imprime.
 *
 * Módulo de 0,254 mm (o `mmBarWidth` do .rtm): 2 pontos numa térmica de
 * 203 dpi. A largura sai do código; o número é encolhido para caber nela.
 *
 * A ALTURA não é a do .rtm (6,2 mm): com ela o leitor da Mercocamp não pegava
 * (26/10). A etiqueta do .exe saía com ~10 mm — o driver esticava. A largura
 * não dá para aumentar: a chave já ocupa até o "Total NF-e".
 */
function Barras({
  texto,
  left,
  top,
  alturaMm,
}: {
  texto: string | null;
  left: number;
  top: number;
  alturaMm: number;
}): JSX.Element | null {
  if (!texto) return null;
  // 128-C: 11 módulos por par de dígitos, mais início, verificador e parada.
  const modulos = 11 * (Math.ceil(texto.length / 2) + (texto.length % 2) + 2) + 13;
  const larguraMm = modulos * 0.254;
  const fonteMm = Math.min(3.2, larguraMm / (texto.length * 0.6));
  return (
    <div style={{ position: 'absolute', left: `${X0 + left / 1000}mm`, top: `${Y0 + top / 1000}mm` }}>
      <CodigoBarras texto={texto} moduloMm={0.254} alturaPt={alturaMm * 2.835} />
      <div
        style={{
          width: `${larguraMm}mm`,
          textAlign: 'center',
          fontFamily: '"Courier New", monospace',
          fontWeight: 700,
          fontSize: `${fonteMm}mm`,
          lineHeight: 1,
          marginTop: '0.4mm',
          whiteSpace: 'nowrap',
        }}
      >
        {texto}
      </div>
    </div>
  );
}

/** Moldura de um dos três blocos do .rtm (TppRegion). */
function Bloco({ top, height }: { top: number; height: number }): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        left: `${X0}mm`,
        top: `${Y0 + top / 1000}mm`,
        width: `${97.367}mm`,
        height: `${height / 1000}mm`,
        border: '0.3mm solid #000',
        boxSizing: 'border-box',
      }}
    />
  );
}

const moeda = (v: number | null): string =>
  v === null ? '' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * A etiqueta de expedição — DANFE simplificado, 100 × 150 mm.
 *
 * Reproduz o TELEVENDAS_ETIQUETAS_EXPEDICAO_DANFE_RESUMIDA.rtm que o
 * ERPM_ETIQUETAS.exe imprime: mesmos campos, mesmas posições, mesmas fontes
 * (lidas do arquivo, não de foto). Uma etiqueta por volume.
 */
export function DanfeSimplificada({ v, teste = false }: { v: VolumeDanfe; teste?: boolean }): JSX.Element {
  return (
    <div
      className="etiqueta"
      style={{ position: 'relative', fontFamily: 'Arial, Helvetica, sans-serif', color: '#000' }}
    >
      {/* Bloco 1 — a nota e o emitente */}
      <Bloco top={529} height={50271} />
      <Texto em={[2646, 2910, 31221, 5419]} pt={13} estilo={{ ...negrito, textDecoration: 'underline' }}>
        {v.nfe}
      </Texto>
      <Texto em={[34660, 2910, 60325, 4191]} pt={10} estilo={faixa}>
        &nbsp;DANFE SIMPLIFICADO - Etiqueta
      </Texto>
      <Texto em={[34660, 8467, 60325, 7938]} pt={9} estilo={{ ...negrito, ...quebra }}>
        {v.nomeRem}
      </Texto>
      <Texto em={[2646, 11113, 14552, 3969]} pt={10}>
        Emissão:
      </Texto>
      <Texto em={[2646, 15346, 21696, 4233]} pt={10} estilo={negrito}>
        {v.movimento}
      </Texto>
      <Texto em={[34660, 17727, 59267, 8202]} pt={8} estilo={quebra}>
        {v.enderecoRem}
      </Texto>
      <Texto em={[2646, 21696, 17198, 4233]} pt={10} estilo={negrito}>
        {v.serie}
      </Texto>
      <Texto em={[2646, 25929, 14288, 3969]} pt={10}>
        1 - Saída
      </Texto>
      <Texto em={[34660, 25929, 18785, 3175]} pt={8}>
        {v.cidadeRem}
      </Texto>
      <Texto em={[57150, 25929, 12435, 3175]} pt={8}>
        {v.estadoRem}
      </Texto>
      <Texto em={[70115, 25929, 23813, 3175]} pt={8}>
        {v.cepRem}
      </Texto>
      <Texto em={[34660, 29633, 28575, 3175]} pt={8}>
        {v.cnpjRem}
      </Texto>
      <Texto em={[64294, 29633, 6615, 3704]} pt={9} estilo={{ textAlign: 'center' }}>
        IE:
      </Texto>
      <Texto em={[73025, 29633, 20108, 3175]} pt={8}>
        {v.ieRem}
      </Texto>
      <Texto em={[2646, 31750, 26723, 3175]} pt={8}>
        CHAVE DE ACESSO
      </Texto>
      <Barras texto={v.chaveNfe} left={2646} top={35300} alturaMm={10} />
      <Texto em={[74083, 36777, 16933, 4233]} pt={10} estilo={{ ...negrito, textDecoration: 'underline' }}>
        Total NF-e
      </Texto>
      <Texto em={[70115, 41804, 21960, 4233]} pt={10} estilo={{ ...negrito, textAlign: 'right' }}>
        {moeda(v.total)}
      </Texto>

      {/* Bloco 2 — destinatário */}
      <Bloco top={50271} height={52652} />
      <Texto em={[1588, 52123, 39423, 4995]} pt={12} estilo={faixa}>
        &nbsp;DESTINATÁRIO
      </Texto>
      <Texto em={[1588, 57679, 92340, 4763]} pt={9} estilo={quebra}>
        {v.nomeDes}
      </Texto>
      <Texto em={[1588, 63236, 92340, 10054]} pt={9} estilo={quebra}>
        {v.enderecoDes}
      </Texto>
      <Texto em={[1588, 73290, 92340, 5556]} pt={9} estilo={quebra}>
        {v.complementoDes}
      </Texto>
      <Texto em={[1588, 79111, 92340, 4763]} pt={9} estilo={quebra}>
        {v.referencia}
      </Texto>
      <Texto em={[1588, 84402, 69321, 3969]} pt={9}>
        {v.cidadeDes}
      </Texto>
      <Texto em={[77523, 84402, 11377, 3969]} pt={9} estilo={{ textAlign: 'right' }}>
        {v.estadoDes}
      </Texto>
      <Texto em={[1588, 90223, 61648, 8202]} pt={9} estilo={quebra}>
        {v.bairroDes}
      </Texto>
      <Texto em={[64294, 90223, 25400, 3969]} pt={9} estilo={{ textAlign: 'right' }}>
        {v.cepDes}
      </Texto>

      {/* Bloco 3 — transportador, pedido e volume */}
      <Bloco top={101865} height={33602} />
      <Texto em={[1588, 103717, 34660, 4233]} pt={10} estilo={faixa}>
        &nbsp;TRANSPORTADOR
      </Texto>
      <Texto em={[71438, 105040, 20902, 6646]} pt={16} estilo={{ ...faixa, textAlign: 'center' }}>
        PV
      </Texto>
      <Texto em={[3704, 108479, 58208, 5027]} pt={10}>
        {v.pedidoMarketplace}
      </Texto>
      <Texto em={[70115, 111500, 23813, 5292]} pt={14} estilo={{ ...negrito, textAlign: 'center' }}>
        {v.televenda}
      </Texto>
      <Texto em={[3704, 114036, 58208, 5027]} pt={11} estilo={negrito}>
        {v.transportadora}
      </Texto>
      <Texto em={[71438, 117211, 20902, 5027]} pt={12} estilo={{ ...faixa, textAlign: 'center' }}>
        VOLUME
      </Texto>
      <Barras texto={v.ean} left={3704} top={119500} alturaMm={11} />
      <Texto em={[71438, 121179, 20902, 6350]} pt={16} estilo={{ ...faixa, textAlign: 'center' }}>
        {v.volume}
      </Texto>

      {teste ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${Y0 + 137}mm`,
            textAlign: 'center',
            fontSize: '11pt',
            fontWeight: 700,
          }}
        >
          *** TESTE DE IMPRESSÃO — SEM VALOR ***
        </div>
      ) : null}
    </div>
  );
}
